import { SimplePDFGenerator } from './pdfGenerator'

// Define interfaces for template data
interface TemplateCommonData {
  companySignatoryName?: string
  companyAddress?: string
  companyLogo?: string
  authorizedPerson?: string
  position?: string
  companySignatory?: string
  organization?: string
  departmentAddress?: string
  departmentName?: string
  tenderRefNo?: string
  date?: string
  tenderBrief?: string
  signatoryName?: string
  designation?: string
  signatureDate?: string
  locationAddition?: string
  minimumLocalContent?: string
}

interface HardwareInstallationData extends TemplateCommonData {
  visibleFieldsConfig: Record<string, boolean>
  company: string
  companyProfileName: string
  customerName: string
  customerAddress: string
  contactPersonName: string
  purchaseOrderNumber?: string
  poDate?: string
  stateOffice?: string
  contactPersonDesignation?: string
  contactPersonNumber?: string
  department?: string
  consigneeName?: string
  consigneeDesignation?: string
  consigneeNumber?: string
  callAlignedDate?: string
  emailId?: string
  faxNumber?: string
  // Legacy flat reportCards (kept for backwards compat)
  reportCards?: Array<{
    visibleFields: Record<string, boolean>
    id: number
    title: string
    itemName: string
    itemGroups: Array<{
      id: number
      fieldValues: {
        makeAndModel?: string
        monitorSerialNumber?: string
        cpuSerialNumber?: string
        serialNumberCommon?: string
        installationStatus?: string
        remarks?: string
      }
    }>
  }>
  // New: per-consignee structure
  consignees?: Array<{
    name: string
    designation: string
    number: string
    assignedEngineerId: string
    assignedEngineerName: string
    callAlignedDate: string
    emailId: string
    faxNumber: string
    reportCards: Array<{
      visibleFields: Record<string, boolean>
      id: number
      title: string
      itemName: string
      quantity: number
      itemGroups: Array<{
        id?: number
        fieldValues: {
          makeAndModel?: string
          monitorSerialNumber?: string
          cpuSerialNumber?: string
          serialNumberCommon?: string
          installationStatus?: string
          remarks?: string
        }
      }>
    }>
  }>
}

// TenderAcceptanceUndertakingTemplateGenerator
export class TAUTemplateGenerator {
  static async generateTenderAcceptancePDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createTenderAcceptanceHTML(data)
      const filename = `Tender_Acceptance_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`

      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createTenderAcceptanceHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }

          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Tender Acceptance Undertaking</div>
            <div class="subtitle">(To be submitted on letterhead of tenderer)</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>

          <!-- Info Section (NOT a table in the PDF) -->
          <div class="info-section">
            <div class="info-row">
              <span class="info-label">Tender Ref No.:</span>
              <span class="info-value">${data.tenderRefNo || 'N/A'}</span>
              <span style="margin-left: auto; font-weight: bold;">Date:</span>
              <span style="margin-left: 10px;">${data.date || ''}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Name of work/services:</span>
              <span class="info-value">${data.tenderBrief || ''}</span>
            </div>
          </div>

          <!-- Content Body -->
          <div class="dear-sir">Dear Sir/Madam,</div>

          <div class="paragraph">1. I/We have downloaded/obtained the tender documents for the above-mentioned tender work.</div>

          <div class="paragraph">2. I/We hereby certified that I/We have read the entire terms and conditions of the tender documents including all Annexure/schedule etc. which form the part of the contract/agreement and I/we shall abide here by the terms, conditions and clauses contained therein.</div>

          <div class="paragraph">3. The corrigendum's issue from time to time by your center to have also been taken into consideration, while submitting this acceptance letter.</div>

          <div class="paragraph">4. I/We hereby unconditionally accept the tender conditions of above-mentioned tender document/corrigendum in its totality/entirety.</div>

          <div class="paragraph">5. I/We certified that all information furnish by our firm is true and correct and, in the event, that the information is found to be incorrect/untrue or found violated then your center shall without giving any notice or reason therefor or summarily reject the bid or terminate the contract.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class BlackListingTemplateGenerator {
  static async generateBlacklistingPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createBlacklistingHTML(data)
      const filename = `Blacklisting_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`

      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createBlacklistingHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">UNDERTAKING REGARDING BLACKLISTING / NON – DEBARMENT</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>


          <!-- Content Body -->
          <div class="paragraph">We hereby confirm and declare that we, <span class="text-bold">${data.companySignatoryName || ''}</span>, is not blacklisted/De-registered/debarred by any Government department/ Public Sector Undertaking/ Private Sector/ or any other agency
for which we have Executed/ Undertaken the works/ Services during the last 5 years.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class MakeInIndiaTemplateGenerator {
  static async generateMakeInIndiaPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createMakeInIndiaHTML(data)
      const filename = `Make_In_India_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`

      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createMakeInIndiaHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Self-Certification under Preference to “MAKE IN INDIA” Policy</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph">In line with Government Public Procurement Order No. P-45021/2/2017-BE-II dt. 15.06.2017, as amended from time to time and as applicable on the date of submission of tender, we hereby certify that we M/s <span class="text-bold">${data.companySignatoryName || ''}</span> are local supplier meeting the requirement of minimum Local content (${data.minimumLocalContent || ''}) as defined in above orders for the material against Tender No <span class="text-bold">${data.tenderRefNo || ''}</span></div>

          <div class="paragraph">Details of location at which local value addition will be made is as follows: <span class="text-bold">${data.locationAddition || ''}</span></div>

          <div class="paragraph">We also understand, false declarations will be in breach of the Code of Integrity under Rule 175(1)(i)(h) of the General Financial Rule for which a bidder or its successors can be debarred for upto two years as per Rule 151 (iii) of the General Financial Rules along with such other actions as may be permissible under law.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class MinimumWagePolicyTemplateGenerator {
  static async generateMinimumWagePolicyPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createMinimumWagePolicyHTML(data)
      const filename = `Minimum_Wage_Policy_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`

      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createMinimumWagePolicyHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Subject: Undertaking regarding acceptance of minimum wage policy</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph">It is hereby confirmed that we have fully complied with all the provisions of all Labour and Industrial Laws as applicable to the workers engaged by us for the above work and we have also paid them wages not less than the minimum wages under the relevant laws.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class LandBorderSharingDeclarationTemplateGenerator {
  static async generateLandBorderSharingDeclarationPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createLandBorderSharingDeclarationHTML(data)
      const filename = `Land_Border_Sharing_Declaration_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`

      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createLandBorderSharingDeclarationHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Land Border Sharing Declaration</div>
            <span>(To be submitted in the bidder’s letter head)</span>
          </div>

          <div class="title-section">In-line with Department of Expenditure’s (DoE) Public Procurement Division Order vide ref. F.No.6/18/2019-PPD dated 23.07.2020 & 24.7.2020</div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">Tender No: ${data.tenderRefNo || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph">“I/ we have read the clauses pertaining to Department of Expenditure’s (DoE) Public Procurement Division Order (Public procurement no 1, 2 & 3 vide ref. F.No.6/18/2019-PPD dated 23.07.2020 & 24.7.2020) regarding restrictions on procurement from a bidder of a country which shares a land border with India. I/We hereby certify that I/ we the bidder <span class="text-bold">${data.companySignatoryName || ''}</span> is / are</div>
          <div class="paragraph">a) Not from such a country and eligible to be considered for this tender.</div>
          <div class="paragraph">OR</div>
          <div class="paragraph">b) From such country, has been registered with the competent authority and eligible to be considered for this tender. (Evidence of valid registration by the competent authority shall be attached)</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class ConfirmationOfNoRelativeTemplateGenerator {
  static async generateConfirmationOfNoRelativePDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createConfirmationOfNoRelativeHTML(data)
      const filename = `Confirmation_of_No_Relative_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createConfirmationOfNoRelativeHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Undertaking for Tender - Confirmation of No Relative in Tendering Authority</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="paragraph"><span class="text-bold">Tender Reference No :</span> ${data.tenderRefNo || ''}</div>
            <div class="paragraph"><span class="text-bold">Name of Work :</span> ${data.tenderBrief || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph">I/We, <span class="text-bold">${data.companySignatoryName || ''}</span>, hereby undertake and declare that:</div>
          <div class="paragraph">I/We have read and understood the tender requirements and conditions, and hereby confirm that I/We meet all the necessary criteria and qualifications for the tender.</div>
          <div class="paragraph">I/We hereby declare that I/We have no relative(s) in the tendering authority or in any other organization that is involved in the evaluation, selection, or award of this tender.</div>
          <div class="paragraph">I/We acknowledge that providing false or misleading information in this undertaking may result in disqualification from the tender process, and may lead to legal action against me/us.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class PowerOfAttorneyTemplateGenerator {
  static async generatePowerOfAttorneyPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createPowerOfAttorneyHTML(data)
      const filename = `Power_of_Attorney_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createPowerOfAttorneyHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Power of Attorney</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph">KNOW ALL MEN BY THESE PRESENTS that we, <span class="text-bold">${data.companySignatoryName || ''}</span>,a company/organization duly organized and existing under the laws of <span class="text-bold">India</span>, with its principal office
at <span class="text-bold">${data.departmentAddress || ''}</span>, do hereby constitute, appoint and authorize Mr./Ms. ${data.authorizedPerson || ''}, who is presently employed with us and holding the position of ${data.position || ''}, as our
attorney-in-fact, to act for us and in our name, place and stead, with respect to the following matters:
</div>

          <div class="paragraph">To provide information and responses to <span class="text-bold">${data.departmentName || ''}</span>, and to attend meetings and negotiations with <span class="text-bold">${data.departmentName || ''}</span> and any other third parties relating to the bid.</div>
          <div class="paragraph">To receive notices, letters and other communications from <span class="text-bold">${data.departmentName || ''}</span> and to represent us in all matters concerning the tender for the said work</div>
          <div class="paragraph">We hereby agree to ratify and confirm all acts, deeds and things lawfully done by our said attorney pursuant to this Power of Attorney and that all acts, deeds and things done by our aforesaid attorney shall and shall always be deemed to have been done by us.</div>
          <div class="paragraph">This Power of Attorney shall be valid and effective from the date of execution and shall continue until the completion of the said work or until revoked by us in writing.</div>
          <div class="paragraph">IN WITNESS WHEREOF, we have executed this Power of Attorney on the <span class="text-bold">${data.signatureDate || ''}</span>.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class BidSecurityDeclarationTemplateGenerator {
  static async generateBidSecurityDeclarationPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createBidSecurityDeclarationHTML(data)
      const filename = `Bid_Security_Declaration_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createBidSecurityDeclarationHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="paragraph"><span class="text-bold">Date:</span> ${data.signatureDate || ''}</div>
          <div class="title-section">
            <div class="title">Bid Security Declaration</div>
            <div class="paragraph">(On Bidders’ Letter Head)</div>
          </div>

          <!-- Content Body -->

          <div class="paragraph">I /We, the authorized signatory of M/s <span class="text-bold">${data.companySignatoryName || ''}</span>, participating in the subject Tender No: ${data.tenderRefNo || ''} for the job of ${data.tenderBrief || ''}, do hereby declare that in the event:</div>

          <div class="paragraph">I / We withdraw / modify our bid during the period of bid validity</div>

          <div class="paragraph">OR</div>

          <div class="paragraph">I/We commit any other breach of tender conditions/ contract which would have otherwise attracted forfeiture of EMD</div>

          <div class="paragraph">OR</div>

          <div class="paragraph">I / We fail to / refuse to initiate the execution of the awarded Contract as per the terms of the Contract then I / We could be suspended from being eligible for bidding / award of all future tender(s) for a period as applicable per the Incident Management Policy of GeM.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class NoPendingLitigationDeclarationTemplateGenerator {
  static async generateNoPendingLitigationDeclarationPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createNoPendingLitigationDeclarationHTML(data)
      const filename = `No_Pending_Litigation_Declaration_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createNoPendingLitigationDeclarationHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">Declaration of No Pending Litigation</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph">I, <span class="text-bold">${data.signatoryName || ''}</span>,  do hereby solemnly affirm and declare as follows:</div>

          <div class="paragraph">1. I am the <span class="text-bold">${data.designation || ''}</span> of the firm/company known as <span class="text-bold">${data.companySignatoryName || ''}</span> having its registered office at <span class="text-bold">${data.companyAddress || ''}</span></div>

          <div class="paragraph">2. I hereby certify that, as of <span class="text-bold">${data.signatureDate || ''}</span> there are no arbitration or litigation cases pending or initiated against <span class="text-bold">${data.companySignatoryName || ''}</span> in any court, tribunal, or arbitration proceedings.</div>

          <div class="paragraph">3. I further declare that there are no legal disputes, claims, or controversies that could potentially lead to arbitration or litigation against <span class="text-bold">${data.companySignatoryName || ''}</span>.</div>

          <div class="paragraph">4. I understand the importance of providing accurate and truthful information, and I affirm that the statements made in this affidavit are true and correct to the best of my knowledge, information, and belief.</div>

          <div class="paragraph">5. I undertake to promptly inform the relevant parties involved in any project or contract in the event that any arbitration or litigation case is initiated against <span class="text-bold">${data.companySignatoryName || ''}</span>.</div>

          <div class="paragraph">6. I understand that any false statement in this affidavit may lead to legal consequences, and I am making this affidavit in good faith.</div>

          <div class="paragraph">7. This affidavit is executed on <span class="text-bold">${data.signatureDate || ''}</span>.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class NonBankruptcyDeclarationTemplateGenerator {
  static async generateNonBankruptcyDeclarationPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createNonBankruptcyDeclarationHTML(data)
      const filename = `Non_Bankruptcy_Declaration_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createNonBankruptcyDeclarationHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .text-right {
            text-align: right;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="paragraph text-right"><span class="text-bold">Date:</span> ${data.signatureDate || ''}</div>
          <div class="title-section">
            <div class="title">DECLARATION OF NON-BANKRUPTCY</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph"><span class="text-bold">Subject:</span> Declaration of Non-Bankruptcy</div>

          <div class="paragraph">I/We, the undersigned, <span class="text-bold">${data.companySignatoryName || ''}</span> having our registered office at <span class="text-bold">${data.companyAddress || ''}</span>, do hereby solemnly declare and affirm that:</div>
          <div>Non-Bankruptcy Status:</div>
          <div class="paragraph">As of the date of this declaration, <span class="text-bold">${data.companySignatoryName || ''}</span> is not bankrupt, insolvent, or undergoing any bankruptcy, insolvency, or liquidation proceedings under any applicable laws.</div>
          <div>Legal Compliance:</div>
          <div class="paragraph"><span class="text-bold">${data.companySignatoryName || ''}</span> is not subject to any court proceedings, creditor arrangements, or similar legal actions that may affect our financial standing or ability to fulfill contractual obligations.</div>
          <div>Truthfulness and Accuracy:</div>
          <div class="paragraph">This declaration is made in full honesty, and I/we understand that any false statement or misrepresentation may lead to disqualification from the bidding process and legal consequences.</div>
          <div class="paragraph">I/We make this declaration with full understanding and acknowledge that it may be used as evidence of our eligibility to participate in the bidding process.</div>
          <div class="paragraph">Signed on this <span class="text-bold">${data.signatureDate || ''}</span> at <span class="text-bold">${data.companyAddress || ''}</span>.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class NonLiquidationDeclarationTemplateGenerator {
  static async generateNonLiquidationDeclarationPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createNonLiquidationDeclarationHTML(data)
      const filename = `Non_Liquidation_Declaration_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createNonLiquidationDeclarationHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="title-section">
            <div class="title">NON LIQUIDATION DECLARATION</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph"><span class="text-bold">SUBJECT: NON LIQUIDATION DECLARATION</span></div>

          <div class="paragraph">This is to certify that <span class="text-bold">${data.companySignatoryName || ''}</span> having its registered office at <span class="text-bold">${data.companyAddress || ''}</span>  has never been under liquidation / De-registered / debarred / suspended by any Government department / Public Sector Undertaking / Private Sector / or any other agency for which we have Executed / Undertaken the works / Services.</div>
          <div class="paragraph">Kindly accept this declaration.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class NonDisclosureDeclarationTemplateGenerator {
  static async generateNonDisclosureDeclarationPDF(data: TemplateCommonData): Promise<void> {
    try {
      const htmlContent = this.createNonDisclosureDeclarationHTML(data)
      const filename = `Non_Disclosure_Declaration_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createNonDisclosureDeclarationHTML(data: TemplateCommonData): string {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <style>
          body {
            font-family: 'Switzer', Times, serif !important;
            color: #000000;
            padding: 40px 150px;
            margin: 0;
            line-height: 1.5;
            -webkit-font-smoothing: antialiased;
            -moz-osx-font-smoothing: grayscale;
            text-rendering: optimizeLegibility;
            font-weight: normal;
          }
          .content {
            max-width: 800px;
            margin: 0 auto;
          }

          /* Header Section with company name and logo */
          .header-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 10px;
            border-bottom: 2px solid #000;
            padding-bottom: 10px;
          }

          .company-info {
            flex: 1;
          }

          .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #0066cc;
            margin: 0 0 5px 0;
          }

          .company-address {
            font-size: 11px;
            margin: 0;
            line-height: 1.4;
          }

          .logo-container {
            text-align: right;
            flex-shrink: 0;
          }

          .logo-image {
            max-height: 60px;
            max-width: 150px;
          }

          /* Title Section */
          .title-section {
            text-align: center;
            margin: 30px 0;
          }

          .title {
            font-weight: bold;
            text-decoration: underline;
            font-size: 16px;
            margin: 0 0 5px 0;
          }

          .subtitle {
            font-size: 11px;
            font-style: italic;
            margin: 0;
          }

          /* To Section */
          .to-section {
            margin: 30px 0;
          }

          .to-label {
            font-weight: bold;
            margin-bottom: 5px;
          }

          .organization {
            font-weight: bold;
            margin-bottom: 3px;
          }

          .department-address {
            font-size: 12px;
            line-height: 1.4;
          }

          /* Info Section - NOT a table but formatted text */
          .info-section {
            margin: 25px 0 30px 0;
          }

          .info-row {
            margin-bottom: 8px;
            display: flex;
            align-items: baseline;
          }

          .info-label {
            font-weight: bold;
            display: inline-block;
            min-width: 160px;
          }

          .info-value {
            display: inline-block;
          }

          /* Content paragraphs */
          .paragraph {
            margin: 15px 0;
            text-align: justify;
          }

          .dear-sir {
            font-weight: bold;
            margin: 20px 0 15px 0;
          }

          .thanking {
            font-weight: bold;
            margin: 20px 0 10px 0;
          }

          /* Signature Section */
          .signature-section {
            margin-top: 20px;
          }

          .signature-image {
            max-height: 80px;
            margin-bottom: 10px;
          }

          .signatory-name {
            font-weight: bold;
            margin: 5px 0;
          }

          .designation {
            margin: 3px 0;
          }

          .for-company {
            margin: 3px 0;
          }

          .signature-date {
            margin: 8px 0 0 0;
          }

          .text-bold {
            font-weight: bold;}
        </style>
      </head>
      <body>
        <div class="content">
          <!-- Header with company name, address and logo -->
          <div class="header-wrapper">
            <div class="company-info">
              <div class="company-name">${data.companySignatoryName || ''}</div>
              <div class="company-address">${data.companyAddress || ''}</div>
            </div>

            ${
              data.companyLogo
                ? `
            <div class="logo-container">
              <img src="${data.companyLogo}" class="logo-image">
            </div>`
                : ''
            }
          </div>

          <!-- Title Section -->
          <div class="paragraph text-right"><span class="text-bold">Date:</span> ${data.signatureDate || ''}</div>

          <div class="title-section">
            <div class="title">NON-DISCLOSURE DECLARATION</div>
          </div>

          <!-- To Section -->
          <div class="to-section">
            <div class="to-label">To,</div>
            <div class="organization">${data.organization || ''}</div>
            <div class="department-address">${data.departmentAddress || ''}</div>
          </div>

          <!-- Content Body -->
          <div class="paragraph"><span class="text-bold">Subject: NON-DISCLOSURE DECLARATION</span></div>

          <div class="paragraph">I/We, the undersigned, on behalf of ${data.companySignatoryName || ''}, having our registered office at ${data.companyAddress || ''}, hereby declare that we fully understand and acknowledge that in the course of our participation in the above-referenced tender, we may gain access to confidential and proprietary information, including but not limited to technical specifications, financial details, project documents, evaluation criteria, or other sensitive information (collectively referred to as "Confidential Information").</div>

          <div class="paragraph"><span class="text-bold">We hereby undertake and agree to the following:</span></div>

          <div class="paragraph">We shall keep all such Confidential Information strictly confidential and shall not disclose, reproduce, or distribute it in any form to any third party without the prior written consent of the procuring entity</div>

          <div class="paragraph">We shall use the Confidential Information solely for the purpose of preparing and submitting our tender offer.</div>

          <div class="paragraph">We shall ensure that all personnel or representatives involved in the tender process on our behalf are made aware of and agree to comply with the terms of this declaration.</div>

          <div class="paragraph">This declaration shall remain valid and binding throughout the tender process and, if we are not the successful bidder, shall remain in force for a period of starting from the tender closing date. We acknowledge that a breach of this declaration may result in disqualification from the tender process and/or legal action as deemed appropriate by the procuring entity</div>

          <div class="paragraph">This declaration is made in good faith and is duly authorized by the undersigned representative of the bidder.</div>

          <div class="thanking">Thanking You,</div>

          <!-- Signature Section -->
          <div class="signature-section">
            ${data.companySignatory ? `<img src="${data.companySignatory}" class="signature-image">` : ''}
            <div class="signatory-name">${data.signatoryName || ''}</div>
            <div class="designation">${data.designation || ''}</div>
            <div class="for-company">For ${data.companySignatoryName || ''}</div>
            <div class="signature-date">Date: ${data.signatureDate || ''}</div>
          </div>
        </div>
      </body>
      </html>
    `
  }
}

export class HardwareInstallationReportTemplateGenerator {
  static async generateHardwareInstallationPDF(data: HardwareInstallationData): Promise<void> {
    try {
      const htmlContent = this.createHardwareInstallationHTML(data)
      const filename = `Hardware_Installation_Report_${data.tenderRefNo || ''}_${new Date().toISOString().split('T')[0]}.pdf`
      await SimplePDFGenerator.generatePDF(htmlContent, filename)
    } catch (error) {
      console.error('Error generating PDF:', error)
      throw error
    }
  }

  private static createHardwareInstallationHTML(data: HardwareInstallationData): string {
    const getCompanyDetails = () => {
      if (data.company === 'maxstationary' ||
        data.companyProfileName?.includes('Max Stationary')) {
        return {
          name: 'Max stationary',
          address: 'Flat No 3, Giridhar Apartment, Panchavati Road, Panchavati Colony, Nashik, Maharashtra - 422003',
          email: 'rajushirsat1980@gmail.com',
          phone: '8793660007'
        }
      } else if (data.company === 'ostnasik' ||
        data.companyProfileName?.includes('O.S. TECH VISION')) {
        return {
          name: 'O.S. TECH VISION (INDIA) PRIVATE LIMITED',
          address: 'Flat No 3, Giridhar Apartment, Panchavati Road, Panchavati Colony, Nashik, Maharashtra - 422003',
          email: 'rajushirsat1980@gmail.com',
          phone: '8793660007'
        }
      } else {
        return {
          name: 'SHRADDHA ENTERPRISES',
          address: 'Preeti Pushp, Plot No-12, Ambad-ITI Link Road, Kartikiya Nagar, Kamatwade, Ambad, Nashik - 422010',
          email: 'rajushirsat1980@gmail.com',
          phone: '8793660007'
        }
      }
    }

    const company = getCompanyDetails()

    const formatDate = (dateStr?: string): string => {
      if (!dateStr) return ''
      try {
        const date = new Date(dateStr)
        return date.toLocaleDateString('en-IN', {
          day: '2-digit', month: '2-digit', year: 'numeric'
        }).replace(/\//g, '-')
      } catch { return dateStr }
    }

    const TH = 'padding:2px 4px 13px;border-right:1px solid #000;border-bottom:1px solid #000;background-color:#d3d3d3;font-weight:bold;text-align:center;font-size:9px;'
    const TD = 'text-align:center;padding:2px 4px 13px;border-right:1px solid #000;border-bottom:1px solid #000;font-size:9px;'
    const TD_LEFT = 'text-align:left;padding:2px 4px 13px;border-right:1px solid #000;border-bottom:1px solid #000;font-size:9px;'
    const LBL = 'padding:2px 4px 13px;border-right:1px solid #000;border-bottom:1px solid #000;font-size:10px;font-weight:bold;background-color:#f0f0f0;'
    const VAL = 'padding:2px 4px 13px;border-right:1px solid #000;border-bottom:1px solid #000;font-size:10px;'

    // ── Resolve consignees list (new structure) or fall back to legacy flat mode ─
    const consignees = data.consignees && data.consignees.length > 0
      ? data.consignees
      : [{
          name: data.consigneeName || '',
          designation: data.consigneeDesignation || '',
          number: data.consigneeNumber || '',
          assignedEngineerId: '',
          assignedEngineerName: '',
          callAlignedDate: data.callAlignedDate || '',
          emailId: data.emailId || '',
          faxNumber: data.faxNumber || '',
          reportCards: data.reportCards || []
        }]

    // ── Render one full report section per consignee ───────────────────────────
    const renderConsigneeReport = (
      consignee: typeof consignees[0],
      globalPageOffset: number,
      totalPagesAll: number
    ): { html: string; pageCount: number } => {
      const cards = consignee.reportCards || []

      const hasAnyBothMonitorAndCpu = cards.some(card =>
        (card.itemGroups || []).some(g =>
          (g.fieldValues?.monitorSerialNumber || '').trim() !== '' &&
          (g.fieldValues?.cpuSerialNumber || '').trim() !== ''
        )
      )

      const tableHeader = `
        <thead>
          <tr>
            <th style="${TH}width:5%">SR.NO</th>
            <th style="${TH}width:30%">ITEM NAME &amp; CONFIG</th>
            ${hasAnyBothMonitorAndCpu
              ? `<th style="${TH}">MONITOR SERIAL NO</th><th style="${TH}">CPU SERIAL NO</th>`
              : `<th colspan="2" style="${TH}">SERIAL NUMBER</th>`
            }
            <th style="${TH}">INSTALLATION (Y/N)</th>
            <th style="${TH}">REMARK</th>
          </tr>
        </thead>`

      interface RowSlot {
        srNo: number
        cardItemName: string
        groupIndex: number
        totalGroups: number
        hasBothCols: boolean
        fv: Record<string, string | undefined>
      }

      const slots: RowSlot[] = []
      let srNo = 1

      cards.forEach(card => {
        const itemGroups = card.itemGroups || []
        const hasBothMonitorAndCpu = itemGroups.some(g =>
          (g.fieldValues?.monitorSerialNumber || '').trim() !== '' &&
          (g.fieldValues?.cpuSerialNumber || '').trim() !== ''
        )
        itemGroups.forEach((group, index) => {
          slots.push({
            srNo,
            cardItemName: card.itemName || '',
            groupIndex: index,
            totalGroups: itemGroups.length,
            hasBothCols: hasBothMonitorAndCpu,
            fv: group.fieldValues || {}
          })
        })
        srNo++
      })

      const ROWS_PAGE_1 = 16
      const ROWS_PAGE_N = 22

      const pages: RowSlot[][] = []
      let slotIdx = 0
      let pageIdx = 0
      while (slotIdx < slots.length) {
        const capacity = pageIdx === 0 ? ROWS_PAGE_1 : ROWS_PAGE_N
        pages.push(slots.slice(slotIdx, slotIdx + capacity))
        slotIdx += capacity
        pageIdx++
      }
      if (pages.length === 0) pages.push([])
      const consigneePageCount = pages.length

      const renderPageRows = (pageSlots: RowSlot[]): string => {
        let html = ''
        let i = 0
        while (i < pageSlots.length) {
          const slot = pageSlots[i]
          const { srNo, cardItemName, hasBothCols, fv } = slot

          let rowspanOnPage = 1
          while (
            i + rowspanOnPage < pageSlots.length &&
            pageSlots[i + rowspanOnPage].srNo === srNo
          ) { rowspanOnPage++ }

          if (hasBothCols) {
            html += `<tr>
              <td rowspan="${rowspanOnPage}" style="${TD}">${srNo}</td>
              <td rowspan="${rowspanOnPage}" style="${TD_LEFT}">${(cardItemName || '').replace(/\n/g, '<br>')}</td>
              <td style="${TD}">${fv.monitorSerialNumber || ''}</td>
              <td style="${TD}">${fv.cpuSerialNumber || ''}</td>
              <td style="${TD}">${fv.installationStatus || ''}</td>
              <td style="${TD}">${fv.remarks || ''}</td>
            </tr>`
            for (let j = 1; j < rowspanOnPage; j++) {
              const sfv = pageSlots[i + j].fv
              html += `<tr>
                <td style="${TD}">${sfv.monitorSerialNumber || ''}</td>
                <td style="${TD}">${sfv.cpuSerialNumber || ''}</td>
                <td style="${TD}">${sfv.installationStatus || ''}</td>
                <td style="${TD}">${sfv.remarks || ''}</td>
              </tr>`
            }
          } else {
            const serialValue = (fv.serialNumberCommon || '').trim() ||
              (fv.monitorSerialNumber || '').trim() ||
              (fv.cpuSerialNumber || '').trim() || ''
            html += `<tr>
              <td rowspan="${rowspanOnPage}" style="${TD}">${srNo}</td>
              <td rowspan="${rowspanOnPage}" style="${TD_LEFT}">${(cardItemName || '').replace(/\n/g, '<br>')}</td>
              <td colspan="2" style="${TD}">${serialValue}</td>
              <td style="${TD}">${fv.installationStatus || ''}</td>
              <td style="${TD}">${fv.remarks || ''}</td>
            </tr>`
            for (let j = 1; j < rowspanOnPage; j++) {
              const sfv = pageSlots[i + j].fv
              const sv2 = (sfv.serialNumberCommon || '').trim() ||
                (sfv.monitorSerialNumber || '').trim() ||
                (sfv.cpuSerialNumber || '').trim() || ''
              html += `<tr>
                <td colspan="2" style="${TD}">${sv2}</td>
                <td style="${TD}">${sfv.installationStatus || ''}</td>
                <td style="${TD}">${sfv.remarks || ''}</td>
              </tr>`
            }
          }
          i += rowspanOnPage
        }
        return html
      }

      const renderedPages = pages.map((pageSlots, idx) => {
        const localPageNum = idx + 1
        const globalPageNum = globalPageOffset + localPageNum
        const isFirst = localPageNum === 1
        const isLast = localPageNum === consigneePageCount
        const bodyRows = renderPageRows(pageSlots)

        return `
<div class="page" style="width:100%;margin:0 auto 20px auto;background:white;page-break-after:always;">

  ${isFirst ? `
  <div style="text-align:center;border-bottom:2px solid #000;padding-bottom:8px;">
    <div style="font-size:18px;font-weight:bold;color:#1a3a5c;text-transform:uppercase;margin-bottom:4px;">${company.name}</div>
    <div style="font-size:9px;color:#444;line-height:1.6;">
      Address: ${company.address}<br>
      Email: ${company.email} | Contact No: ${company.phone}
    </div>
  </div>

  <div style="text-align:center;font-size:15px;font-weight:bold;text-transform:uppercase;border-bottom:2px solid #000;padding-bottom:15px;margin-bottom:12px;letter-spacing:1px;">
    HARDWARE INSTALLATION REPORT
  </div>

  <table style="width:100%;border-collapse:collapse;border-top:1px solid #000;border-left:1px solid #000;margin-bottom:14px;">
    <tr>
      <td style="${LBL}width:28%">Tender Ref No.</td>
      <td style="${VAL}">${data.tenderRefNo || ''}</td>
      <td style="${LBL}width:10%">Date:</td>
      <td style="${VAL}">${formatDate(data.signatureDate || data.date)}</td>
    </tr>
    <tr>
      <td style="${LBL}">Purchase Order No.</td>
      <td style="${VAL}">${data.purchaseOrderNumber || ''}</td>
      <td style="${LBL}">PO Date:</td>
      <td style="${VAL}">${formatDate(data.poDate)}</td>
    </tr>
    <tr>
      <td style="${LBL}">Customer Name</td>
      <td colspan="3" style="${VAL}">${data.customerName || ''}</td>
    </tr>
    <tr>
      <td style="${LBL}">Customer Address</td>
      <td colspan="3" style="${VAL}">${data.customerAddress || ''}</td>
    </tr>
    <tr>
      <td style="${LBL}">State Office</td>
      <td colspan="3" style="${VAL}">${data.stateOffice || ''}</td>
    </tr>
    <tr>
      <td style="${LBL}">Contact Person &amp; Designation &amp; Number</td>
      <td colspan="3" style="${VAL}">${data.contactPersonName || ''}${data.contactPersonDesignation ? ' - ' + data.contactPersonDesignation : ''}${data.contactPersonNumber ? ' - ' + data.contactPersonNumber : ''}</td>
    </tr>
    <tr>
      <td colspan="4" style="text-align:center;font-weight:bold;background-color:#d3d3d3;padding:5px 8px 15px;border-right:1px solid #000;border-bottom:1px solid #000;font-size:10px;">CONSIGNEE NAME</td>
    </tr>
    <tr>
      <td style="${LBL}">Consignee Name &amp; Designation</td>
      <td colspan="3" style="${VAL}">${consignee.name || ''}${consignee.designation ? ' - ' + consignee.designation : ''}</td>
    </tr>
    <tr>
      <td style="${LBL}">Contact Number</td>
      <td style="${VAL}">${consignee.number || ''}</td>
      <td style="${LBL}">Engineer</td>
      <td style="${VAL}">${consignee.assignedEngineerName || ''}</td>
    </tr>
    <tr>
      <td style="${LBL}">Department</td>
      <td colspan="3" style="${VAL}">${data.department || ''}</td>
    </tr>
    <tr>
      <td style="${LBL}">Call Aligned Date</td>
      <td colspan="3" style="${VAL}">${formatDate(consignee.callAlignedDate)}</td>
    </tr>
    <tr>
      <td style="${LBL}">Email ID</td>
      <td style="${VAL}">${consignee.emailId || ''}</td>
      <td style="${LBL}">FAX No</td>
      <td style="${VAL}">${consignee.faxNumber || ''}</td>
    </tr>
  </table>` : `
  <div style="text-align:right;font-size:10px;font-style:italic;color:#666;margin-bottom:10px;">
    Hardware Installation Report — ${consignee.name || 'Consignee'} (Continued — Page ${localPageNum} of ${consigneePageCount})
  </div>`}

  <table style="width:100%;border-collapse:collapse;border-top:1px solid #000;border-left:1px solid #000;margin-bottom:0;">
    ${tableHeader}
    <tbody>${bodyRows}</tbody>
  </table>

  ${isLast ? `
  <div style="border:1px solid #000;border-top:none;padding:5px 8px 15px;font-size:10px;">
    Call Closure Note: I/We Confirm that Above Mentioned Installation is Completed and Tested According
  </div>
  <table style="width:100%;border-collapse:collapse;border:1px solid #000;border-top:none;min-height:80px;">
    <tr>
      <td style="width:50%;padding:0 10px 15px;border-right:1px solid #000;font-size:10px;font-style:italic;color:#333;vertical-align:bottom;">
        Signature of Engineer with Date<br><br><br><br>
        ${consignee.assignedEngineerName ? `<strong>${consignee.assignedEngineerName}</strong>` : ''}
      </td>
      <td style="width:50%;padding:0 10px 15px;font-size:10px;font-style:italic;color:#333;vertical-align:bottom;">
        Stamp and Signature of Customer<br><br><br><br>
        ${consignee.name ? `<strong>${consignee.name}</strong>${consignee.designation ? ' - ' + consignee.designation : ''}` : ''}
      </td>
    </tr>
  </table>` : ''}

  <div style="text-align:center;font-size:8px;color:#666;margin-top:6px;">Page ${globalPageNum} of ${totalPagesAll}</div>
</div>`
      }).join('\n')

      return { html: renderedPages, pageCount: consigneePageCount }
    }

    // ── Calculate total pages across all consignees ────────────────────────────
    const calcPageCount = (cons: typeof consignees[0]): number => {
      const cards = cons.reportCards || []
      let totalSlots = 0
      cards.forEach(card => { totalSlots += (card.itemGroups || []).length })
      if (totalSlots === 0) return 1
      let pages = 0, slotIdx = 0, pageIdx = 0
      while (slotIdx < totalSlots) {
        const cap = pageIdx === 0 ? 16 : 22
        slotIdx += cap; pages++; pageIdx++
      }
      return pages
    }

    const totalPagesAll = consignees.reduce((sum, c) => sum + calcPageCount(c), 0)

    // ── Generate HTML for every consignee ─────────────────────────────────────
    let allPagesHtml = ''
    let globalPageOffset = 0
    consignees.forEach(consignee => {
      const { html, pageCount } = renderConsigneeReport(consignee, globalPageOffset, totalPagesAll)
      allPagesHtml += html
      globalPageOffset += pageCount
    })

    return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Hardware Installation Report</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: 'Arial', sans-serif; background: #fff; color: #000; padding: 20px; font-size: 12px; width: 210mm; }
    @media print { body { padding: 0; width: 210mm; } }
  </style>
</head>
<body>
${allPagesHtml}
</body>
</html>`
  }
}
